import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';
import type {
  StudentRecognitionResponse,
  ManualAwardPayload,
  CoachPanelResponse,
  CelebrationData,
  StarEvent,
} from '../types';

export function useGetStudentRecognitionState(
  meetingId: string,
  occurenceId: string,
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  return useQuery<StudentRecognitionResponse>(
    ['studentRecognitionState', meetingId, occurenceId],
    async () => {
      const res = await ChessRestClient.post('classroom/v1/get_student_recognition_state/', {
        meetingId,
        occurenceId,
      });
      return res.data;
    },
    {
      enabled: options?.enabled ?? true,
      refetchInterval: options?.refetchInterval ?? 5000,
    },
  );
}

export function useAwardManualStar(
  onSuccess?: (data: { success: boolean; total_stars: number; award_type: string }) => void,
  onError?: (error: unknown) => void,
) {
  return useMutation(
    async (payload: ManualAwardPayload) => {
      const res = await ChessRestClient.post('classroom/v1/award_manual_star/', payload);
      return res.data;
    },
    { onSuccess, onError },
  );
}

export function useSetPuzzleMysteryBonus(
  onSuccess?: (data: { success: boolean; mysteryCount: number }) => void,
  onError?: (error: unknown) => void,
) {
  return useMutation(
    async (payload: { meetingId: string; occurenceId: string; puzzleId: number; isMystery: boolean }) => {
      const res = await ChessRestClient.post('classroom/v1/set_puzzle_mystery_bonus/', payload);
      return res.data;
    },
    { onSuccess, onError },
  );
}

export interface AwardConfigEntry {
  event_type: string;
  label: string;
  default_label: string;
  is_enabled: boolean;
  icon_url: string | null;
  is_manual: boolean;
}

export function useGetTournamentAwardConfig(
  tournamentId: number | null,
  options?: { enabled?: boolean },
) {
  return useQuery<{ success: boolean; config: AwardConfigEntry[] }>(
    ['tournamentAwardConfig', tournamentId],
    async () => {
      const res = await ChessRestClient.post('classroom/v1/get_tournament_award_config/', { tournamentId });
      return res.data;
    },
    { enabled: (options?.enabled ?? true) && tournamentId != null },
  );
}

export function useGetCoachRecognitionPanel(
  meetingId: string,
  occurenceId: string,
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  return useQuery<CoachPanelResponse>(
    ['coachRecognitionPanel', meetingId, occurenceId],
    async () => {
      const res = await ChessRestClient.post('classroom/v1/get_coach_recognition_panel/', {
        meetingId,
        occurenceId,
      });
      return res.data;
    },
    {
      enabled: options?.enabled ?? true,
      refetchInterval: options?.refetchInterval ?? 10000,
    },
  );
}

export function useComputeEndCelebration(
  onSuccess?: (data: { success: boolean; celebration: CelebrationData }) => void,
  onError?: (error: unknown) => void,
) {
  return useMutation(
    async (payload: { meetingId: string; occurenceId: string }) => {
      const res = await ChessRestClient.post('classroom/v1/compute_end_celebration/', payload);
      return res.data;
    },
    { onSuccess, onError },
  );
}

export function useApproveEndCelebration(
  onSuccess?: (data: any) => void,
  onError?: (error: unknown) => void,
) {
  return useMutation(
    async (payload: {
      meetingId: string;
      occurenceId: string;
      awardsData: CelebrationData;
      coachStarStudentId?: number;
      autoAwardAssignments?: Record<string, number>;
      extraStars?: {studentId: number; count: number}[];
      extraAwards?: {studentId: number; awardType: string; count: number}[];
    }) => {
      const res = await ChessRestClient.post('classroom/v1/approve_end_celebration/', payload);
      return res.data;
    },
    { onSuccess, onError },
  );
}

export function useGetEndCelebrationDisplay(
  meetingId: string,
  occurenceId: string,
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  return useQuery<{ success: boolean; celebration: CelebrationData | null }>(
    ['endCelebrationDisplay', meetingId, occurenceId],
    async () => {
      const res = await ChessRestClient.post('classroom/v1/get_end_celebration_display/', { meetingId, occurenceId });
      return res.data;
    },
    { enabled: options?.enabled ?? false, refetchInterval: options?.refetchInterval },
  );
}

export interface AwardFeedEvent {
  id: number;
  user_id: number;
  name: string;
  event_type: string;
  stars_awarded: number;
  puzzle_id: number | null;
  created_at: string;
  icon_url?: string | null;
  label_override?: string | null;
}

export function useGetClassroomAwardFeed(
  meetingId: string,
  occurenceId: string,
  options?: { enabled?: boolean; refetchInterval?: number; limit?: number },
) {
  return useQuery<{ success: boolean; events: AwardFeedEvent[] }>(
    ['classroomAwardFeed', meetingId, occurenceId, options?.limit ?? 50],
    async () => {
      const res = await ChessRestClient.post('classroom/v1/get_classroom_award_feed/', {
        meetingId,
        occurenceId,
        limit: options?.limit ?? 50,
      });
      return res.data;
    },
    {
      enabled: options?.enabled ?? true,
      refetchInterval: options?.refetchInterval ?? 5000,
    },
  );
}

export interface LiveAttemptRow {
  user_id: number;
  name: string;
  status: 'correct' | 'wrong' | 'in_progress' | 'not_attempted';
  solve_time: string | null;
  attempts: number;
  wrong_moves?: string[];
  correct_move?: string | null;
  stars_earned?: number;
  session_stars?: number;
  tournament_wins?: number;
}

export function useGetClassroomLiveAttempts(
  meetingId: string,
  occurenceId: string,
  puzzleId: number | null,
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  return useQuery<{ success: boolean; puzzleId: number; attempts: LiveAttemptRow[] }>(
    ['classroomLiveAttempts', meetingId, occurenceId, puzzleId],
    async () => {
      const res = await ChessRestClient.post('classroom/v1/get_classroom_live_attempts/', {
        meetingId,
        occurenceId,
        puzzleId,
      });
      return res.data;
    },
    {
      enabled: (options?.enabled ?? true) && puzzleId != null,
      refetchInterval: options?.refetchInterval ?? 3000,
    },
  );
}

export function useGetStudentStarEvents(
  meetingId: string,
  occurenceId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<{ success: boolean; events: StarEvent[] }>(
    ['studentStarEvents', meetingId, occurenceId],
    async () => {
      const res = await ChessRestClient.post('classroom/v1/get_student_star_events/', { meetingId, occurenceId });
      return res.data;
    },
    { enabled: options?.enabled ?? true },
  );
}

export interface StudentSessionStats {
  success: boolean;
  puzzles_solved: number;
  puzzles_attempted: number;
  total_puzzles: number;
  wrong_moves: Array<{position_id: number; moves: string; attempted_index: number}>;
  homework_completed: boolean;
}

export function useGetStudentSessionStats(
  meetingId: string,
  occurenceId: string,
  studentUserId: number | null,
  options?: {enabled?: boolean},
) {
  return useQuery<StudentSessionStats>(
    ['studentSessionStats', meetingId, occurenceId, studentUserId],
    async () => {
      const res = await ChessRestClient.post('classroom/v1/get_student_session_stats/', {
        meetingId,
        occurenceId,
        studentUserId,
      });
      return res.data;
    },
    {enabled: (options?.enabled ?? true) && studentUserId != null},
  );
}

// ───── Coach insights: live alerts, class brief, longitudinal ─────

export type CoachAlert =
  | {type: 'idle'; user_id: number; name: string; idle_seconds: number}
  | {type: 'stuck'; user_id: number; name: string; puzzle_id: number; attempts: number}
  | {type: 'speed_outlier'; user_id: number; name: string; solve_time: string}
  | {type: 'common_wrong_move'; puzzle_id: number; move: string; count: number}
  | {type: 'disengaged'; user_id: number; name: string};

export interface CoachLiveAlertsResponse {
  success: boolean;
  alerts: CoachAlert[];
}

export function useGetCoachLiveAlerts(
  meetingId: string,
  occurenceId: string,
  puzzleId: number | null,
  options?: {enabled?: boolean; refetchInterval?: number; meetingRole?: string},
) {
  return useQuery<CoachLiveAlertsResponse>(
    ['coachLiveAlerts', meetingId, occurenceId, puzzleId],
    async () => {
      const body: any = {meetingId, occurenceId};
      if (puzzleId != null) body.puzzleId = puzzleId;
      if (options?.meetingRole) body.meetingRole = options.meetingRole;
      const res = await ChessRestClient.post('classroom/v1/get_coach_live_alerts/', body);
      return res.data;
    },
    {
      enabled: (options?.enabled ?? true) && !!meetingId && !!occurenceId,
      refetchInterval: options?.refetchInterval ?? 5000,
    },
  );
}

export interface ClassBriefResponse {
  success: boolean;
  hardest_puzzle: null | {
    puzzle_id: number; solve_pct: number; avg_attempts: number;
    common_wrong: string | null; common_wrong_count: number;
    puzzle_name?: string;
    order_index?: number | null;
    starting_fen?: string | null;
    orientation?: string | null;
    move_list?: string[] | null;
  };
  top_mover: null | {user_id: number; name: string; stars: number};
  at_risk: Array<{user_id: number; name: string; reason: string}>;
  class_avg_solve_time: number | null;
  total_solves: number;
  puzzle_count: number;
  student_count: number;
  summary: string;
  homework?: null | {
    assignment_book_id: number;
    assignment_date: string | null;
    puzzle_count: number;
    completed_count: number;
    total_students: number;
    per_student: Array<{
      user_id: number;
      name: string;
      latest_completed: boolean;
      recent_completed: number;
      recent_total: number;
      history: Array<{date: string | null; completed: boolean}>;
    }>;
  };
}

export function useGetClassBrief(
  meetingId: string,
  occurenceId: string,
  options?: {enabled?: boolean; meetingRole?: string},
) {
  return useQuery<ClassBriefResponse>(
    ['classBrief', meetingId, occurenceId],
    async () => {
      const res = await ChessRestClient.post('classroom/v1/get_class_brief/', {
        meetingId, occurenceId, meetingRole: options?.meetingRole,
      });
      return res.data;
    },
    {enabled: (options?.enabled ?? true) && !!meetingId && !!occurenceId},
  );
}

export interface LongitudinalResponse {
  success: boolean;
  sessions: Array<{webinar_id: string; occurence_id: string; date: string; stars: number; solves: number; wins: number}>;
  trajectory: number[];
  tournament_winrate: number;
  total_wins: number;
  total_stars_all_time: number;
}

export function useGetStudentLongitudinal(
  meetingId: string,
  occurenceId: string,
  studentUserId: number | null,
  limitSessions = 5,
  options?: {enabled?: boolean; meetingRole?: string},
) {
  return useQuery<LongitudinalResponse>(
    ['studentLongitudinal', meetingId, occurenceId, studentUserId, limitSessions],
    async () => {
      const res = await ChessRestClient.post('classroom/v1/get_student_longitudinal/', {
        meetingId, occurenceId, studentUserId, limitSessions, meetingRole: options?.meetingRole,
      });
      return res.data;
    },
    {enabled: (options?.enabled ?? true) && studentUserId != null && !!meetingId && !!occurenceId},
  );
}

export function useClassroomGameWon() {
  const queryClient = useQueryClient();
  return useMutation(
    async (params: {meetingId: string; occurenceId: string; tournamentGameId: number}) => {
      const res = await ChessRestClient.post('classroom/v1/classroom_game_won/', params);
      return res.data;
    },
    {
      onSuccess: (_data, vars) => {
        queryClient.invalidateQueries(['studentRecognitionState', vars.meetingId, vars.occurenceId]);
      },
    },
  );
}
